/**
 * Thin wrapper around the conversation action endpoints, adding a toast +
 * refetch on each call so the detail components stay lean. Every method
 * resolves to `true` on success / `false` on failure (already toasted).
 */
import { useCallback, useState } from 'react'
import type { MergeResult } from './MergeDrawer.js'
import { api, type ApiError } from '../../../api/client.js'
import { useToast } from '../../../components/system/ToastProvider.js'

/** Human-readable copy for each merge-eligibility error code the API returns. */
const MERGE_ERROR_MESSAGES: Record<string, string> = {
  SELF_MERGE_NOT_ALLOWED: 'A conversation cannot be merged into itself.',
  NO_SECONDARY_CONVERSATIONS: 'Select at least one conversation to merge.',
  PRIMARY_CONVERSATION_NOT_FOUND: 'The primary conversation no longer exists.',
  SECONDARY_CONVERSATION_NOT_FOUND: 'One of the selected conversations no longer exists.',
  PRIMARY_IS_SECONDARY: 'This conversation has already been merged into another conversation.',
  ALREADY_SECONDARY: 'A selected conversation has already been merged into another conversation.',
  ALREADY_PRIMARY: 'A selected conversation is already a primary of other merged conversations.',
  IN_PARENT_CHILD_RELATION: 'Parent/child conversations cannot be merged.',
  IN_LINK_RELATION: 'Linked conversations cannot be merged.',
  APPROVAL_PENDING: 'Conversations awaiting approval cannot be merged.',
  DIFFERENT_CLIENT:
    'These conversations belong to different requesters. Tick “Merge anyway” to continue, and consider adding their recipients to CC.',
  TOO_MANY_SECONDARIES: 'You can merge at most 15 conversations at a time.',
}

/** Options mirroring the merge dialog's checkboxes. */
export interface MergeOptions {
  /** Classification field keys copied primary→secondary. */
  updateFields?: string[]
  reason?: string
  /** Note added to the primary; defaults to a generated summary. */
  note?: string
  /** Public notes are visible to the requester; default is an internal note. */
  noteIsPublic?: boolean
  /** Carry the secondaries' recipients onto the primary as CC. */
  addSecondaryRecipientsToCc?: boolean
  /** Confirms an intentional cross-requester merge. */
  allowDifferentRequesters?: boolean
  /** Move the secondaries' messages/notes here. Default true. */
  moveConversations?: boolean
  /** Close each secondary. Default true. */
  closeSecondaries?: boolean
}

export interface TransferInput {
  transferType: 'cold' | 'warm' | 'queue'
  toAgentId?: string
  toQueueId?: string
  toTeamId?: string
  reason?: string
  description?: string
  isPrivate?: boolean
  cc?: string[]
}

export interface UseConversationActionsReturn {
  busy: boolean
  close: (resolutionType: 'resolved' | 'merged' | 'cancelled', notes?: string, customFields?: Record<string, unknown>) => Promise<boolean>
  reopen: () => Promise<boolean>
  onHold: (reason?: string) => Promise<boolean>
  transfer: (input: TransferInput) => Promise<boolean>
  completeWarm: (newPrimaryAgentId: string) => Promise<boolean>
  addCoAgent: (agentId: string) => Promise<boolean>
  removeCoAgent: (agentId: string) => Promise<boolean>
  link: (conversationBId: string, reason?: string) => Promise<boolean>
  /**
   * Merge one or more secondary conversations INTO this conversation (the primary
   * survivor). Their conversations move here and they are closed, a note is
   * written on both sides, and — with `addSecondaryRecipientsToCc` — their
   * requesters are carried over as CC. See ConversationEngine.merge.
   */
  merge: (secondaryConversationIds: string[], options?: MergeOptions) => Promise<MergeResult>
  /**
   * Same as `merge`, but the primary is chosen explicitly rather than being
   * this conversation — Freshdesk lets the agent nominate any conversation in the
   * selected set as the survivor.
   */
  mergeInto: (
    primaryConversationId: string,
    secondaryConversationIds: string[],
    options?: MergeOptions,
  ) => Promise<MergeResult>
  setParent: (parentConversationId: string) => Promise<boolean>
  /** Make `childId` a child of this conversation (sets the child's parent). */
  addChild: (childId: string) => Promise<boolean>
  /**
   * @param message overrides the success toast. Callers that know the chosen
   *   label ("Priority set to High") should pass one — the derived fallback can
   *   only name the field, since the patch carries ids rather than labels.
   */
  updateProps: (patch: Record<string, unknown>, message?: string) => Promise<boolean>
}

/** Human labels for the patch keys the properties panel sends. */
const PROPERTY_LABELS: Record<string, string> = {
  priorityId: 'Priority',
  conversationStatusId: 'Status',
  statusId: 'Status',
  categoryId: 'Category',
  subcategoryId: 'Sub category',
  typeId: 'Type',
  stateId: 'State',
  impactId: 'Impact',
  urgencyId: 'Urgency',
  complexityId: 'Complexity',
  assignedAgentId: 'Assignee',
  assignedQueueId: 'Queue',
  assignedTeamId: 'Team',
  campaignId: 'Campaign',
  tags: 'Tags',
  subject: 'Subject',
  description: 'Description',
}

/**
 * Names what a patch changed, so the toast says something useful.
 *
 * A bare "Updated" left the agent guessing whether the click registered on the
 * field they meant. The patch carries ids, not labels, so this can only name
 * the fields — callers that know the chosen value pass a fuller message.
 */
export function describePatch(patch: Record<string, unknown>): string {
  const names = Object.keys(patch).map((k) => PROPERTY_LABELS[k] ?? k)
  if (names.length === 0) return 'Updated'
  if (names.length === 1) return `${names[0]} updated`
  if (names.length === 2) return `${names[0]} and ${names[1]} updated`
  return `${names.length} properties updated`
}

export function useConversationActions(
  conversationId: string | null,
  onChanged: () => void,
): UseConversationActionsReturn {
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const run = useCallback(
    async (
      fn: () => Promise<unknown>,
      successMsg: string,
      /** Optional per-action override for the failure toast. Return null to
       *  fall back to the generic "Action failed" message. */
      formatError?: (e: unknown) => { title: string; detail: string } | null,
    ): Promise<boolean> => {
      if (!conversationId) return false
      setBusy(true)
      try {
        await fn()
        toast.success(successMsg)
        onChanged()
        return true
      } catch (e) {
        const custom = formatError?.(e) ?? null
        if (custom) toast.error(custom.title, custom.detail)
        else toast.error('Action failed', e instanceof Error ? e.message : 'Unexpected error')
        return false
      } finally {
        setBusy(false)
      }
    },
    [conversationId, onChanged, toast],
  )

  const close = useCallback<UseConversationActionsReturn['close']>(
    (resolutionType, notes, customFields) =>
      run(
        () =>
          api.post(`/conversations/${conversationId}/close`, {
            resolutionType,
            ...(notes ? { notes } : {}),
            ...(customFields ? { customFields } : {}),
          }),
        'Conversation closed',
        // A parent conversation can't close while a child is open (409). Name the
        // blocking conversations so the agent knows exactly what to close first.
        (e) => {
          const body = (e as ApiError | undefined)?.body as
            | { error?: string; openChildren?: Array<{ referenceId?: string }> }
            | undefined
          if (body?.error !== 'OPEN_CHILD_TICKETS') return null
          const refs = (body.openChildren ?? [])
            .map((c) => c.referenceId)
            .filter((r): r is string => Boolean(r))
          return {
            title: 'Cannot close — open child conversations',
            detail: refs.length
              ? `Close ${refs.length === 1 ? 'this child conversation' : 'these child conversations'} first: ${refs.join(', ')}`
              : 'This conversation has open child conversations. Close all child conversations first.',
          }
        },
      ),
    [conversationId, run],
  )

  const reopen = useCallback<UseConversationActionsReturn['reopen']>(
    () => run(() => api.post(`/conversations/${conversationId}/reopen`, {}), 'Conversation reopened'),
    [conversationId, run],
  )

  const onHold = useCallback<UseConversationActionsReturn['onHold']>(
    (reason) =>
      run(
        () => api.post(`/conversations/${conversationId}/on-hold`, reason ? { reason } : {}),
        'Conversation placed on hold',
      ),
    [conversationId, run],
  )

  const transfer = useCallback<UseConversationActionsReturn['transfer']>(
    (input) =>
      run(
        () =>
          api.post(`/conversations/${conversationId}/transfer`, {
            transferType: input.transferType,
            ...(input.toAgentId ? { toAgentId: input.toAgentId } : {}),
            ...(input.toQueueId ? { toQueueId: input.toQueueId } : {}),
            ...(input.toTeamId ? { toTeamId: input.toTeamId } : {}),
            ...(input.reason ? { reason: input.reason } : {}),
            ...(input.description ? { description: input.description } : {}),
            ...(input.isPrivate !== undefined ? { isPrivate: input.isPrivate } : {}),
            ...(input.cc ? { cc: input.cc } : {}),
          }),
        'Conversation transferred',
      ),
    [conversationId, run],
  )

  const completeWarm = useCallback<UseConversationActionsReturn['completeWarm']>(
    (newPrimaryAgentId) =>
      run(
        () => api.post(`/conversations/${conversationId}/complete-warm`, { newPrimaryAgentId }),
        'Warm transfer completed',
      ),
    [conversationId, run],
  )

  const addCoAgent = useCallback<UseConversationActionsReturn['addCoAgent']>(
    (agentId) =>
      run(() => api.post(`/conversations/${conversationId}/co-agents`, { agentId }), 'Collaborator added'),
    [conversationId, run],
  )

  const removeCoAgent = useCallback<UseConversationActionsReturn['removeCoAgent']>(
    (agentId) =>
      run(
        () => api.delete(`/conversations/${conversationId}/co-agents/${agentId}`),
        'Collaborator removed',
      ),
    [conversationId, run],
  )

  const link = useCallback<UseConversationActionsReturn['link']>(
    (conversationBId, reason) =>
      run(
        () =>
          api.post(`/conversations/${conversationId}/link`, {
            conversationBId,
            ...(reason ? { reason } : {}),
          }),
        'Conversation linked',
      ),
    [conversationId, run],
  )

  // Merges don't go through `run`: a rejection has to reach the dialog, not
  // just a toast, so the agent can see which conversation was refused and why
  // while the form is still in front of them.
  const runMerge = useCallback(
    async (
      primaryId: string,
      secondaryConversationIds: string[],
      options?: MergeOptions,
    ): Promise<MergeResult> => {
      setBusy(true)
      try {
        await api.post(`/conversations/${primaryId}/merge`, {
          secondaryConversationIds,
          ...(options?.updateFields?.length ? { updateFields: options.updateFields } : {}),
          ...(options?.reason ? { reason: options.reason } : {}),
          ...(options?.note ? { note: options.note } : {}),
          ...(options?.noteIsPublic !== undefined ? { noteIsPublic: options.noteIsPublic } : {}),
          ...(options?.addSecondaryRecipientsToCc !== undefined
            ? { addSecondaryRecipientsToCc: options.addSecondaryRecipientsToCc }
            : {}),
          ...(options?.allowDifferentRequesters !== undefined
            ? { allowDifferentRequesters: options.allowDifferentRequesters }
            : {}),
          ...(options?.moveConversations !== undefined
            ? { moveConversations: options.moveConversations }
            : {}),
          ...(options?.closeSecondaries !== undefined
            ? { closeSecondaries: options.closeSecondaries }
            : {}),
        })
        toast.success('Conversations merged')
        onChanged()
        return { ok: true }
      } catch (e) {
        // Merge eligibility failures come back as a stable error code (409/400)
        // and, where the rule concerns one conversation, that conversation's id.
        // Both are handed back so the dialog can point at the right row.
        const body = (e as ApiError | undefined)?.body as
          | { error?: string; conversationId?: string }
          | undefined
        const code = body?.error
        const msg = code ? MERGE_ERROR_MESSAGES[code] : undefined
        if (msg) toast.error('Cannot merge', msg)
        else toast.error('Action failed', e instanceof Error ? e.message : 'Unexpected error')
        return {
          ok: false,
          failure: {
            ...(code !== undefined ? { code } : {}),
            ...(body?.conversationId !== undefined ? { conversationId: body.conversationId } : {}),
          },
        }
      } finally {
        setBusy(false)
      }
    },
    [onChanged, toast],
  )

  const merge = useCallback<UseConversationActionsReturn['merge']>(
    (secondaryConversationIds, options) =>
      runMerge(conversationId ?? '', secondaryConversationIds, options),
    [conversationId, runMerge],
  )

  const mergeInto = useCallback<UseConversationActionsReturn['mergeInto']>(
    (primaryConversationId, secondaryConversationIds, options) =>
      runMerge(primaryConversationId, secondaryConversationIds, options),
    [runMerge],
  )

  const setParent = useCallback<UseConversationActionsReturn['setParent']>(
    (parentConversationId) =>
      run(
        () => api.post(`/conversations/${conversationId}/parent`, { parentConversationId }),
        'Parent conversation set',
      ),
    [conversationId, run],
  )

  // Make another conversation a CHILD of this one → set the child's parent to us.
  const addChild = useCallback<UseConversationActionsReturn['addChild']>(
    (childId) =>
      run(
        () => api.post(`/conversations/${childId}/parent`, { parentConversationId: conversationId }),
        'Child conversation added',
      ),
    [conversationId, run],
  )

  const updateProps = useCallback<UseConversationActionsReturn['updateProps']>(
    (patch, message) =>
      run(() => api.patch(`/conversations/${conversationId}`, patch), message ?? describePatch(patch)),
    [conversationId, run],
  )

  return {
    busy,
    close,
    reopen,
    onHold,
    transfer,
    completeWarm,
    addCoAgent,
    removeCoAgent,
    link,
    merge,
    mergeInto,
    setParent,
    addChild,
    updateProps,
  }
}
